import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  CardMedia,
  Input,
  TextField,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { UploadMedia } from "../../utils/mediaUpload";
import { isAlphabetic, isString, isValidInput } from "../../utils/validations";
import PhoneInput from "react-phone-input-2";
import { useGetAllRolesQuery } from "../../services/accessRole";
import {
  useAddCustomerMutation,
  useLazyGetCustomerByIdQuery,
  useUpdateCustomerMutation,
} from "../../services/customer";
import { showError, showToast } from "../../constants/toasts";
import { SubAdmin } from "../../types/General";
import { ROLES } from "../../utils/enums";
import useAuth from "../../hooks/useAuth";

const AddSubadmin = () => {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id as string;
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [dialCode, setDialCode] = useState("+971");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState(false);
  const user = useAuth();
  const [addCustomer] = useAddCustomerMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [getCustomerById] = useLazyGetCustomerByIdQuery();
  const { data: roles } = useGetAllRolesQuery({
    limit: 10,
    page: 1,
    search: "",
  });
  const [selectField1, setSelectField1] = React.useState("0");
  const handleChange1 = (event: SelectChangeEvent) => {
    setSelectField1(event.target.value);
  };

  const handleChangePhone = (phone: any, country: any) => {
    setPhone(phone?.replace(country.dialCode, ""));
    setDialCode(country?.dialCode);
  };

  const getSubAdminById = async () => {
    setLoading(true);
    try {
      const response = await getCustomerById({
        id,
      }).unwrap();

      if (response?.statusCode === 200) {
        // setCustomerDetail(response?.data || null);
        formik.setFieldValue("name", response?.data?.fullName);
        formik.setFieldValue("email", response?.data?.email);
        setDialCode(response?.data?.countryCode.slice(1));
        setPhone(response?.data?.phone);
        setImage(response?.data?.image);
        setSelectField1(response?.data?.accessRole?._id);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("This field is required")
        .min(2, "Minimum 2 characters are required"),
      email: Yup.string()
        .required("Email is required!")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Please enter a valid email address!"
        ),
    }),
    onSubmit: async (values) => {
      if (!phone || phone.length < 8 || selectField1 === "0") {
        return;
      }

      let body: SubAdmin & { userId?: string };

      if (id) {
        body = {
          fullName: values.name,
          accessRole: selectField1,
          ...(image && { image }),
          phone: phone,
          countryCode: "+" + dialCode,
          email: values.email,
          userId: id,
        };
      } else {
        body = {
          fullName: values.name,
          role: ROLES.SUB_ADMIN,
          accessRole: selectField1,
          ...(image && { image }),
          phone: phone,
          countryCode: "+" + dialCode,
          email: values.email,
        };
      }

      try {
        setLoading(true);
        let response;
        if (id) {
          // If ID exists, update customer
          response = await updateCustomer(body).unwrap();
        } else {
          // Else, add customer
          response = await addCustomer(body).unwrap();
        }
        if (response?.statusCode === 200) {
          setLoading(false);
          showToast(
            id
              ? "Sub-Admin updated successfully"
              : "Sub-Admin added successfully"
          );
          navigate("/manage-subadmin");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.log(error);
      }
    },
  });

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setImage("");
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setImage(imageUrl?.data || "");
    }
  };

  useEffect(() => {
    if (user?.role === ROLES.SUB_ADMIN) {
      navigate("/permission-denied", { replace: true });
    }
  }, [user]);

  useEffect(() => {
    if (id) getSubAdminById();
  }, [id]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{id ? "Edit" : "Add"} Sub-Admin</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-subadmin")}>
            Manage Sub-Admin
          </span>{" "}
          - {id ? "Edit" : "Add"} Sub-Admin
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Upload Image</label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={handleImageUpload}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
              {/* {!image && error ? (
                <h6 className="err_msg">Image is required</h6>
              ) : (
                ""
              )} */}
            </div>
            <div className="control_group w_33">
              <label>Name</label>

              <TextField
                placeholder="Enter Name"
                variant="outlined"
                className="text_field"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                inputProps={{ maxLength: 80 }}
                value={formik.values.name}
                onChange={(val) => {
                  if (
                    isValidInput(val.target.value) &&
                    isString(val.target.value)
                  ) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
            <div className="control_group w_33">
              <label>Email Id</label>
              <TextField
                placeholder="Enter Email Id"
                hiddenLabel
                fullWidth
                className="text_field"
                name="email"
                id="email"
                inputProps={{ maxLength: 80 }}
                value={formik.values.email}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.email && formik.errors.email}
              />
            </div>
            <div className="control_group w_33">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={dialCode + phone}
                country={"uae"}
                inputClass="phoneInput"
                onChange={(phone, country) => handleChangePhone(phone, country)}
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
              />
              {!phone && error ? (
                <h6 className="err_msg">Phone Number is required</h6>
              ) : (
                ""
              )}
              {phone.length >= 1 && phone.length < 8 && error ? (
                <h6 className="err_msg">Please enter a valid phone number</h6>
              ) : (
                ""
              )}
            </div>
            <div className="control_group w_33">
              <label>Role</label>
              <Select fullWidth value={selectField1} onChange={handleChange1}>
                {/* <option disabled value={'0'}>Select Role</option> */}
                <MenuItem disabled value={"0"}>
                  Select Role
                </MenuItem>
                {roles?.data?.SubAdminAccess.map((item, i) => (
                  <MenuItem value={item?._id}>{item.name}</MenuItem>
                ))}
              </Select>
              {selectField1 === "0" && error ? (
                <h6 className="err_msg">Please select a role</h6>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="form_btn">
            <Button
              type="submit"
              onClick={() => setError(true)}
              className="btn btn_primary"
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddSubadmin;
